import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const AGENT_HIGHLIGHT_PARAM = 'agentHighlight';

const HIGHLIGHT_CLASS = 'agent-highlight-flash';

function safeSelectorValue(key: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(key);
  }
  return key.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * When URL contains agentHighlight=..., scroll to matching [data-agent-highlight] and run a short flash animation.
 * Retries briefly so targets that mount after navigation (e.g. profile query) still resolve.
 */
export function useAgentHighlightFromLocation(): void {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get(AGENT_HIGHLIGHT_PARAM)?.trim();
    if (!key) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const tryOnce = (): boolean => {
      const el = document.querySelector(`[data-agent-highlight="${safeSelectorValue(key)}"]`);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      el.classList.add(HIGHLIGHT_CLASS);
      window.setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), 2100);
      params.delete(AGENT_HIGHLIGHT_PARAM);
      const qs = params.toString();
      const next = `${location.pathname}${qs ? `?${qs}` : ''}${location.hash || ''}`;
      window.history.replaceState(null, '', next);
      return true;
    };

    const tick = () => {
      if (cancelled) return;
      if (tryOnce()) return;
      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tick, 120);
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);
}
